import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ActionButton,
  ButtonModel,
  DrawerContext,
} from '@notino/react-styleguide';

import { Product } from '@containers/ProductDetailContainer/model';
import { LoadingWrapper } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/LoadingWrapper';
import { ReviewsSkeleton } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/ReviewsSkeleton';
import { ReviewSummarySkeleton } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/ReviewSummarySkeleton';
import { messages } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/messages';
import { ReviewsItems } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems';
import {
  createReviewFiltersStore,
  ReviewFiltersContextProvider,
} from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/SortFilterBar/Stores/filtersStore';
import { SortBox } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/styled';
import { ReviewsSummary } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewsSummary';
import { ErrorMessage } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/styled';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { CenterWrapper } from '@containers/ProductDetailContainer/styled';
import { AllReviewPhotos } from '@lib/reviews/AllReviewPhotos';
import { useLoggedInUserReviewFetch } from '@queries/useReviews/useLoggedInUserReviewFetch';
import {
  hasImageReviewsFromData,
  useReviewsFetch,
} from '@queries/useReviews/useReviewsFetch';
import { useReviewsSummaryFetch } from '@queries/useReviews/useReviewsSummaryFetch';

import { REVIEW_SECTION_ID } from '../constants';
import { headMetaMessages } from '../HeadMeta/messages';
import { ReviewsJsonLd } from '../ReviewsJsonLd';

import { ReviewCount, ReviewsWrapper, TitleWrapper } from './styled';

interface IReviewsComponentProps {
  productCode: string;
  product: Product;
  selectedVariant: Product['variants'][number];
  imageDomainPath: string;
  masterId: string;
  isAllReviewsPage?: boolean;
}

const ReviewsContent: FC<IReviewsComponentProps> = ({
  masterId,
  productCode,
  isAllReviewsPage,
  imageDomainPath,
  product,
  selectedVariant,
}) => {
  const {
    product: { catalogMasterId },
  } = useProductDetailContext();
  const {
    data: reviewsSummaryData,
    loading: reviewsSummaryLoading,
    error: reviewsSummaryError,
    refetch: reviewsSummaryRefetch,
  } = useReviewsSummaryFetch(catalogMasterId, {
    ssr: true,
  });

  const {
    data,
    error,
    loading,
    refetch: reviewsRefetch,
    fetchMore,
  } = useReviewsFetch(catalogMasterId, {
    ssr: true,
  });

  const { data: loggedInUserReviewData, refetch: refetchLoggedInUserReview } =
    useLoggedInUserReviewFetch(productCode, { ssr: true });

  const { formatMessage } = useIntl();

  const productName = formatMessage(headMetaMessages.review, {
    brandName: product.brand?.name ?? '',
    collection: `${product.collection ?? ''}`,
  });

  const reviewSummary =
    reviewsSummaryData?.productDetailByCatalogMasterId?.reviewSummary;

  const productReviewCount = product.reviewOverview?.count ?? 0;

  const hasImageReviews = hasImageReviewsFromData(data);

  const reviewsLength = data?.reviewsAndFilters?.data?.reviews.length;

  const reviewCount = reviewsLength && reviewsLength > 0 ? reviewsLength : '';

  return (
    <>
      <ReviewsJsonLd
        product={product}
        selectedVariant={selectedVariant}
        imageDomainPath={imageDomainPath}
        reviews={data as any}
      />

      <ReviewsWrapper id={REVIEW_SECTION_ID}>
        <TitleWrapper>
          {productName}
          <ReviewCount>{reviewCount}</ReviewCount>
        </TitleWrapper>

        <LoadingWrapper
          isLoading={reviewsSummaryLoading}
          isError={!reviewSummary || !!reviewsSummaryError}
          skeleton={<ReviewSummarySkeleton />}
          error={
            <CenterWrapper>
              <ErrorMessage>
                <FormattedMessage {...messages.reviewsSummaryFailed} />
              </ErrorMessage>

              <ActionButton
                onClick={() => reviewsSummaryRefetch()}
                buttonSize={ButtonModel.Sizes.small}
              >
                <FormattedMessage {...messages.tryAgain} />
              </ActionButton>
            </CenterWrapper>
          }
        >
          {reviewSummary && (
            <ReviewsSummary
              productReviewCount={productReviewCount}
              reviewSummary={reviewSummary}
              loggedInUserReview={loggedInUserReviewData?.loggedInUserReview}
              productCode={productCode}
              masterId={masterId}
              catalogMasterId={catalogMasterId}
              isAllReviewsPage={isAllReviewsPage}
              refetchLoggedInUserReview={async () => {
                await refetchLoggedInUserReview({
                  masterProductCode: productCode,
                });
              }}
            />
          )}
        </LoadingWrapper>

        {hasImageReviews && <AllReviewPhotos />}

        <LoadingWrapper
          isLoading={loading}
          isError={!data || !!error}
          skeleton={<ReviewsSkeleton />}
          error={
            <>
              <SortBox />
              <CenterWrapper>
                <ErrorMessage>
                  <FormattedMessage {...messages.reviewsFailed} />
                </ErrorMessage>
                <ActionButton
                  onClick={() => reviewsRefetch()}
                  buttonSize={ButtonModel.Sizes.small}
                >
                  <FormattedMessage {...messages.tryAgain} />
                </ActionButton>
              </CenterWrapper>
            </>
          }
        />

        <ReviewsItems
          productCode={productCode}
          reviewsAndFilters={data?.reviewsAndFilters ?? null}
          reviewsLoading={loading}
          reviewsError={error as any}
          refetch={reviewsRefetch}
          fetchMore={fetchMore}
          productReviewCount={productReviewCount}
          isAllReviewsPage={isAllReviewsPage}
        />
      </ReviewsWrapper>
    </>
  );
};

export const Reviews: FC<IReviewsComponentProps> = (props) => {
  const [store] = React.useState(() => createReviewFiltersStore());

  return (
    <ReviewFiltersContextProvider value={{ store }}>
      <DrawerContext.Provider>
        <ReviewsContent {...props} />
      </DrawerContext.Provider>
    </ReviewFiltersContextProvider>
  );
};
